import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Divider,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Controller } from "react-hook-form";

const EMIJCConformity = ({ control, watch }) => {
  const conformityStatement = watch("conformityStatement");
  const decisionRuleApplicable = watch("decisionRuleApplicable");

  const showDecisionRule = conformityStatement === "Yes";
  const showDecisionRuleOptions =
    showDecisionRule && decisionRuleApplicable === "Applicable";

  const performanceCriteria = watch("performanceCriteria");
  const hasPerformanceCriteria = String(performanceCriteria || "").trim() !== "";

  const customerWitness = watch("customerWitness");
  const showCustomerWitness = customerWitness === "Yes";
  const witness1 = watch("customerWitness1");
  const witness2 = watch("customerWitness2");
  const witness3 = watch("customerWitness3");
  const witness4 = watch("customerWitness4");
  const witness5 = watch("customerWitness5");
  const witness6 = watch("customerWitness6");
  const [witnessCount, setWitnessCount] = useState(3);

  useEffect(() => {
    if (!showCustomerWitness) {
      setWitnessCount(3);
      return;
    }

    const values = [witness1, witness2, witness3, witness4, witness5, witness6];
    let requiredCount = 3;
    values.forEach((value, index) => {
      if (String(value || "").trim() !== "") {
        requiredCount = index + 1;
      }
    });

    setWitnessCount((prev) => Math.max(prev, Math.min(requiredCount, 6)));
  }, [
    showCustomerWitness,
    witness1,
    witness2,
    witness3,
    witness4,
    witness5,
    witness6,
  ]);
  const rowSx = {
    borderBottom: "1px solid",
    borderColor: "divider",
    px: 2,
    py: 1,
  };
  const labelSx = {
    fontWeight: 600,
    color: "text.primary",
  };
  const contentSx = {
    display: "flex",
    alignItems: "flex-start",
    minHeight: 40,
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ px: 2, py: 1.5, bgcolor: "grey.50" }}>
        <Typography variant="h5">Statement of Conformity</Typography>
      </Box>
      <Divider />

      <Grid container alignItems="center" sx={rowSx}>
        <Grid item xs={12} md={4}>
          <Typography variant="body2" sx={labelSx}>
            Statement of Conformity
          </Typography>
        </Grid>
        <Grid item xs={12} md={8} sx={contentSx}>
          <Controller
            name="conformityStatement"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <RadioGroup row aria-label="Statement of Conformity" {...field}>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>
      </Grid>

      {showDecisionRule && (
        <Grid container alignItems="center" sx={rowSx}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" sx={labelSx}>
              If Yes Decision Rule
            </Typography>
          </Grid>
          <Grid item xs={12} md={8} sx={contentSx}>
            <Controller
              name="decisionRuleApplicable"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <RadioGroup row aria-label="If Yes Decision Rule" {...field}>
                  <FormControlLabel
                    value="Applicable"
                    control={<Radio />}
                    label="Applicable"
                  />
                  <FormControlLabel
                    value="Not Applicable"
                    control={<Radio />}
                    label="Not Applicable"
                  />
                </RadioGroup>
              )}
            />
          </Grid>
        </Grid>
      )}

      {showDecisionRuleOptions && (
        <Grid container alignItems="flex-start" sx={rowSx}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" sx={labelSx}>
              Select the Acceptance Rule
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
            }}
          >
            <Controller
              name="decisionRuleOption"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <RadioGroup aria-label="Acceptance Rule" {...field}>
                  <FormControlLabel
                    value="Simple Acceptance"
                    control={<Radio />}
                    sx={{ alignItems: "flex-start", mb: 1 }}
                    label={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 550 }}>
                          Simple acceptance rule without considering Measurement
                          Uncertainty (MU):
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          a) Pass when measured value &lt;= limit specified by
                          the standard.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          b) Fail when measured value &gt; limit specified by
                          the standard.
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="Considering Measurement Uncertainty"
                    control={<Radio />}
                    sx={{ alignItems: "flex-start" }}
                    label={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 550 }}>
                          Acceptance rule considering Measurement Uncertainty
                          (MU):
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          a) Pass when measured value + MU &lt;= limit specified
                          by the standard.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          b) Fail when measured value + MU &gt; limit specified
                          by the standard.
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              )}
            />
          </Grid>
        </Grid>
      )}

      {showDecisionRule && decisionRuleApplicable === "Not Applicable" && (
        <Grid container alignItems="flex-start" sx={rowSx}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" sx={labelSx}>
              Note
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, fontWeight: 550 }}
              color="red"
            >
              If Not Applicable, Statement of Conformity will not be provided.
            </Typography>
          </Grid>
        </Grid>
      )}

      {/* Performance Criteria Note */}
      <Grid container alignItems="flex-start" sx={rowSx}>
        <Grid item xs={12} md={4}>
          <Typography variant="body2" sx={labelSx}>
            Performance Criteria for Immunity Test:
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
          md={8}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textAlign: "left",
          }}
        >
          <Typography variant="body2" sx={{ mt: 0.5 }} color="red">
            During and after the immunity test, the Equipment Under Test (EUT)
            shall continue to operate as intended and shall not exhibit any
            degradation in performance, malfunction, loss of function,
            unintended operation, or change in operating status. The EUT shall
            meet all specified performance requirements throughout the test and
            after the test has been completed.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2 }} color="red">
            Note:
          </Typography>

          <Typography variant="body2" sx={{ mt: 0.5 }} color="red">
            1. It is the responsibility of the customer to provide and clearly
            define the applicable Performance Criteria before the start of
            immunity testing. The Performance Criteria describe how the
            Equipment Under Test (EUT) is expected to perform during and after
            the application of immunity test signals. These criteria form the
            basis for determining whether the EUT has passed or failed the test.
            Without clearly defined Performance Criteria, it is not possible to
            make an objective and technically justified pass/fail assessment.
          </Typography>
          <br />
          <Typography variant="body2" sx={{ mt: 0.25 }} color="red">
            2. The Performance Criteria have been written by the laboratory as a
            general guideline for immunity testing. The customer is responsible
            for defining the final Performance Criteria and may modify, revise,
            or replace these criteria as necessary to reflect the intended
            operation and performance requirements of the Equipment Under Test
            (EUT).
          </Typography>

          <Controller
            name="performanceCriteria"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Performance Criteria *"
                placeholder="Enter the applicable Performance Criteria for the immunity tests"
                multiline
                minRows={4}
                fullWidth
                sx={{ mt: 2 }}
                error={!hasPerformanceCriteria}
                helperText={
                  hasPerformanceCriteria
                    ? ""
                    : "Performance Criteria is mandatory. The 'Next' button stays disabled until this is filled."
                }
              />
            )}
          />
        </Grid>
      </Grid>

      <Grid container alignItems="center" sx={rowSx}>
        <Grid item xs={12} md={4}>
          <Typography variant="body2" sx={labelSx}>
            Test Report
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <FormGroup row sx={{ flexWrap: "wrap", columnGap: 2 }}>
            <Controller
              name="testResultReportRequired"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Report Required"
                />
              )}
            />
            <Controller
              name="testResultReportHardCopy"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Hard Copy"
                />
              )}
            />

            <Controller
              name="certificateSoftCopy"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Soft Copy"
                />
              )}
            />
          </FormGroup>
        </Grid>
      </Grid>

      <Grid container alignItems="center" sx={{ px: 2, py: 1.25 }}>
        <Grid item xs={12} md={4}>
          <Typography variant="body2" sx={labelSx}>
            Customer Witness
          </Typography>
        </Grid>
        <Grid item xs={12} md={8} sx={contentSx}>
          <Controller
            name="customerWitness"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <RadioGroup
                row
                aria-label="Customer Witness"
                {...field}
                sx={{ flexWrap: "nowrap", columnGap: 2 }}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>
      </Grid>

      {showCustomerWitness && (
        <Grid container spacing={2} sx={{ px: 2, py: 1.25 }}>
          <Grid
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Tooltip title="Remove Customer Witness">
              <span>
                <IconButton
                  color="primary"
                  onClick={() =>
                    setWitnessCount((prev) => Math.max(prev - 1, 3))
                  }
                  disabled={witnessCount <= 3}
                  size="small"
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Add Customer Witness">
              <span>
                <IconButton
                  color="primary"
                  onClick={() =>
                    setWitnessCount((prev) => Math.min(prev + 1, 6))
                  }
                  disabled={witnessCount >= 6}
                  size="small"
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>

          {Array.from({ length: witnessCount }, (_, index) => {
            const witnessIndex = index + 1;
            const fieldName = `customerWitness${witnessIndex}`;
            return (
              <Grid item xs={12} md={4} key={fieldName}>
                <Controller
                  name={fieldName}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={`Customer Witness ${witnessIndex}`}
                      fullWidth
                    />
                  )}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default EMIJCConformity;
